import React, { useMemo, useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import QuestionDisplay from "./QuestionDisplay";
import { useTranslation } from "./i18n/useTranslation";

const ENABLE_PRINT_BUTTON = true; // flip to false if you want to hide button

// tiny HTML escaper so user text doesn't break our print HTML
function escapeHtml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// renderSegmentsToHTML tries to mimic SegmentsRendererRN for print.
// We'll keep it simple: text becomes paragraphs, images become block <img>.
function renderSegmentsToHTML(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return "";

  let htmlParts = [];

  segments.forEach((seg) => {
    if (!seg) return;
    if (seg.type === "Text") {
      // replace <br> fragments, etc.
      const safe = escapeHtml(seg.text || "").replace(/\n/g, "<br/>");
      htmlParts.push(`<p class="q-text">${safe}</p>`);
    } else if (seg.type === "Image") {
      const src = seg.base64 || seg.src || seg.uri || "";
      if (src) {
        htmlParts.push(
          `<div class="img-block"><img src="${src}" class="img-block-el" /></div>`
        );
      }
    }
  });

  return htmlParts.join("\n");
}

// Build the full printable HTML document as a string.
function buildPrintableHTML({ testDetails, quiz, t }) {
  // header / meta info
  const title = escapeHtml(testDetails?.title || t("Untitled"));
  const teacherEmail = escapeHtml(testDetails?.teacher_email || "");
  const description = escapeHtml(testDetails?.description || "");
  const duration = testDetails?.duration_minutes
    ? escapeHtml(String(testDetails.duration_minutes))
    : "";
  const startTime = testDetails?.start_time
    ? escapeHtml(new Date(testDetails.start_time).toLocaleString())
    : "N/A";
  const endTime = testDetails?.end_time
    ? escapeHtml(new Date(testDetails.end_time).toLocaleString())
    : "N/A";

  // chapters list if any
  let chaptersHTML = "";
  if (Array.isArray(testDetails?.chapters) && testDetails.chapters.length > 0) {
    const items = testDetails.chapters
      .map((ch) => {
        const cid = escapeHtml(String(ch.chapter_id ?? ""));
        const tid = ch.topic_id
          ? ` → Topic ID: ${escapeHtml(String(ch.topic_id))}`
          : "";
        return `<li>Chapter ID: ${cid}${tid}</li>`;
      })
      .join("");
    chaptersHTML = `
      <div class="meta-line">
        <strong>📚 ${escapeHtml(t("Chapters:"))}</strong>
        <ul class="chapter-list">
          ${items}
        </ul>
      </div>
    `;
  }

  // same renderSegmentsToHTML helper as before
  function renderSegmentsToHTML(segments) {
    if (!Array.isArray(segments) || segments.length === 0) return "";

    let htmlParts = [];

    segments.forEach((seg) => {
      if (!seg) return;
      if (seg.type === "Text") {
        const safe = escapeHtml(seg.text || "").replace(/\n/g, "<br/>");
        htmlParts.push(`<p class="q-text">${safe}</p>`);
      } else if (seg.type === "Image") {
        const src = seg.base64 || seg.src || seg.uri || "";
        if (src) {
          htmlParts.push(
            `<div class="img-block"><img src="${src}" class="img-block-el" /></div>`
          );
        }
      }
    });

    return htmlParts.join("\n");
  }

  // build questions HTML
  function renderOneQuestion(q, idx) {
    if (!q) return "";
    const qNum = idx + 1;
    const qType = parseInt(q.question_type);

    // stem
    let stemHTML = "";
    if (Array.isArray(q?.question?.segments)) {
      stemHTML = renderSegmentsToHTML(q.question.segments);
    } else {
      const textPart = escapeHtml(
        q?.question?.text || t("No question available")
      );
      const imgPart = Array.isArray(q?.question?.images)
        ? q.question.images
            .map((image) => {
              const src = image?.base64 || "";
              if (!src) return "";
              return `<div class="img-block"><img src="${src}" class="img-block-el" /></div>`;
            })
            .join("\n")
        : "";
      stemHTML = `<p class="q-text">${textPart}</p>${imgPart}`;
    }

    let bodyHTML = "";

    if (qType === 1) {
      // fill in the blank
      let opts = q.options;
      if (typeof opts === "string") {
        try {
          opts = JSON.parse(opts);
        } catch {
          /* ignore */
        }
      }

      if (Array.isArray(opts?.word_bank) && opts.word_bank.length > 0) {
        const wbHtml = opts.word_bank
          .map(
            (w) =>
              `<span class="wordbank-chip">${escapeHtml(String(w || ""))}</span>`
          )
          .join(" ");
        bodyHTML += `
          <div class="wordbank-row">
            <strong>🧩 Word Bank:</strong>
            <div class="wordbank-list">${wbHtml}</div>
          </div>
        `;
      }

      bodyHTML += `<div class="answer-line"></div>`;
    } else if (qType === 2) {
      // MCQ
      const OPT_KEYS = ["option1", "option2", "option3", "option4"];
      const optionsHTML = OPT_KEYS.map((optKey, iOpt) => {
        const opt = q.options?.[optKey];
        if (!opt) return "";

        let optContentHTML = "";
        if (Array.isArray(opt?.segments)) {
          optContentHTML = renderSegmentsToHTML(opt.segments);
        } else {
          const optText = escapeHtml(opt?.text || "");
          const optImgs = Array.isArray(opt?.images)
            ? opt.images
                .map((img) => {
                  const src = img?.base64 || "";
                  if (!src) return "";
                  return `<div class="img-block"><img src="${src}" class="img-block-el" /></div>`;
                })
                .join("\n")
            : "";
          optContentHTML = `<div class="opt-text">${optText}</div>${optImgs}`;
        }

        return `
          <div class="option-box">
            <div class="option-label">${escapeHtml(t("Option"))} ${iOpt + 1}:</div>
            <div class="option-body">
              ${optContentHTML}
            </div>
          </div>
        `;
      }).join("\n");

      bodyHTML += optionsHTML;
    } else if (qType === 4) {
      // True / False
      bodyHTML += `
        <div class="option-box">
          <div class="option-body">True □</div>
        </div>
        <div class="option-box">
          <div class="option-body">False □</div>
        </div>
      `;
    } else if (qType === 9) {
      // Matching
      const pairsHTML = Array.isArray(q?.options?.pairs)
        ? q.options.pairs
            .map((pair) => {
              const leftText = escapeHtml(pair?.left?.text || "");
              return `
                <div class="match-row">
                  <div class="match-left">${leftText}</div>
                  <div class="match-arrow">→</div>
                  <div class="match-blank"></div>
                </div>
              `;
            })
            .join("\n")
        : "";
      bodyHTML += pairsHTML;
    } else if (qType === 10 || qType === 11) {
      // Short / Long Essay
      const placeholder =
        qType === 10
          ? t("Write a short response (3–5 sentences)...")
          : t("Write a detailed response (150–200 words)...");
      bodyHTML += `
        <div class="essay-box">
          <div class="essay-placeholder">${escapeHtml(placeholder)}</div>
        </div>
      `;
    }

    return `
      <div class="question-card">
        <div class="question-header">
          <span class="question-number">Q${qNum}.</span>
        </div>
        <div class="question-stem">
          ${stemHTML}
        </div>
        <div class="question-body">
          ${bodyHTML}
        </div>
      </div>
    `;
  }

  const questionsHTML = Array.isArray(quiz)
    ? quiz.map((q, i) => renderOneQuestion(q, i)).join("\n")
    : "";

  // NOTE 👇 we inject a <script> that calls window.print() on load.
  // We don't auto-close the tab; teacher can inspect the PDF first.
  return `
<!DOCTYPE html>
<html>
<head>
<meta charSet="utf-8" />
<title>${title} - ${escapeHtml(t("Test Preview"))}</title>
<style>
  body {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #000;
    background: #fff;
    padding: 24px;
    line-height: 1.4;
    font-size: 14px;
  }
  h1 {
    font-size: 20px;
    margin: 0 0 8px 0;
    font-weight: 600;
  }
  .meta-block {
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 24px;
    background: #f8f8f8;
  }
  .meta-line {
    margin-bottom: 4px;
    font-size: 14px;
  }
  .chapter-list {
    margin: 4px 0 0 16px;
    padding: 0;
  }
  .chapter-list li {
    list-style-type: disc;
    margin-left: 16px;
    margin-bottom: 2px;
    font-size: 14px;
  }
  .question-card {
    border: 1px solid #999;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 24px;
    page-break-inside: avoid;
  }
  .question-header {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 15px;
  }
  .question-number {
    font-weight: 600;
  }
  .question-stem .q-text {
    margin: 0 0 8px 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .img-block {
    text-align: center;
    margin: 8px 0;
  }
  .img-block-el {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
  }
  .question-body {
    margin-top: 12px;
  }
  .wordbank-row {
    margin-bottom: 12px;
    font-size: 14px;
  }
  .wordbank-list {
    margin-top: 4px;
  }
  .wordbank-chip {
    display: inline-block;
    background: #eee;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 2px 6px;
    margin: 2px 4px 2px 0;
    font-size: 13px;
  }
  .answer-line {
    border-bottom: 1px solid #555;
    min-height: 24px;
    margin: 12px 0;
  }
  .option-box {
    border: 1px solid #999;
    border-radius: 6px;
    padding: 8px 10px;
    margin-top: 8px;
    font-size: 14px;
  }
  .option-label {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .option-body {
    font-size: 14px;
    word-break: break-word;
  }
  .opt-text {
    margin-bottom: 4px;
  }
  .match-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    margin-top: 8px;
    gap: 8px;
    font-size: 14px;
  }
  .match-left {
    flex: 0 0 auto;
    font-weight: 500;
    min-width: 120px;
  }
  .match-arrow {
    flex: 0 0 auto;
    font-weight: 600;
  }
  .match-blank {
    flex: 1 1 auto;
    border-bottom: 1px solid #555;
    min-height: 24px;
  }
  .essay-box {
    border: 1px dashed #555;
    border-radius: 6px;
    min-height: 120px;
    margin-top: 12px;
    padding: 8px;
    font-size: 13px;
    color: #666;
    font-style: italic;
    white-space: pre-wrap;
  }
  .essay-placeholder {
    opacity: 0.8;
  }
  @media print {
    body {
      background: #fff !important;
      color: #000 !important;
    }
  }
</style>
</head>
<body>

  <div class="meta-block">
    <h1>🧪 ${title}</h1>

    ${
      teacherEmail
        ? `<div class="meta-line">🧑‍🏫 ${escapeHtml(t("Teacher:"))} ${teacherEmail}</div>`
        : ""
    }

    ${description ? `<div class="meta-line">📝 ${description}</div>` : ""}

    ${
      duration
        ? `<div class="meta-line">🕒 ${escapeHtml(t("Duration:"))} ${duration} ${escapeHtml(
            t("minutes")
          )}</div>`
        : ""
    }

    <div class="meta-line">
      📅 ${escapeHtml(t("Available:"))} ${startTime} – ${endTime}
    </div>

    ${chaptersHTML}
  </div>

  ${questionsHTML}

  <script>
    // auto-trigger print when the new window/tab loads
    window.addEventListener('load', function () {
      window.print();
    });
  </script>
</body>
</html>
  `;
}

export default function TeacherTestPreview({
  visible,
  onClose,
  testDetails = {},
  quiz = [],
  // still let you force dark UI for preview modal if you want,
  // but print will always generate light HTML.
  isDarkMode = false,
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  
  // Responsive modal max width: full on mobile, 85% on tablet, 900px on desktop
  const modalMaxWidth = isMobile ? "100%" : (isTablet ? "85%" : 900);

  const noop = () => {};
  const emptyObj = useMemo(() => ({}), []);
  const scrollWrapperRef = useRef(null);

  // scroll to top when preview opens
  useEffect(() => {
    if (visible && scrollWrapperRef.current?.scrollTo) {
      try {
        scrollWrapperRef.current.scrollTo({ y: 0, animated: false });
      } catch {
        /* ignore */
      }
    }
  }, [visible]);

  // NEW: instead of relying on @media print on the app DOM,
  // we open a brand-new window with static HTML.

function handlePrint() {
  if (Platform.OS !== "web" || typeof window === "undefined") return;

  // scroll preview to top (nice touch but not required)
  if (scrollWrapperRef.current?.scrollTo) {
    scrollWrapperRef.current.scrollTo({ y: 0, animated: false });
  }

  // 1. Build HTML string for the printable version
  const html = buildPrintableHTML({ testDetails, quiz, t });

  // 2. Turn it into a Blob URL
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);

  // 3. Open that Blob URL in a new tab
  const win = window.open(url, "_blank"); // no "noopener,noreferrer" because we WANT it to load that blob

  // (Optional) you can revokeObjectURL later, but most browsers will release it when tab is closed
  // setTimeout(() => URL.revokeObjectURL(url), 60000);
}

  if (!visible) return null;

  // ===== PREVIEW MODAL (same look & feel you liked) =====
  return (
    <View
      style={{
        position: "fixed",
        zIndex: 9999,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDarkMode
          ? "rgba(0,0,0,0.8)"
          : "rgba(0,0,0,0.6)",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
      }}
    >
      <View
        style={{
          width: "100%",
          maxWidth: modalMaxWidth,
          backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow:
            Platform.OS === "web"
              ? "0 20px 40px rgba(0,0,0,0.4)"
              : undefined,
          borderWidth: Platform.OS === "web" ? 1 : 0,
          borderColor: "#00000022",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Toolbar (Close + Print) */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingHorizontal: 16,
            paddingVertical: 12,
            backgroundColor: isDarkMode ? "#374151" : "#f3f4f6",
            borderBottomWidth: 1,
            borderColor: "#d1d5db",
          }}
        >
          <Text
            style={{
              fontWeight: "600",
              fontSize: 16,
              color: isDarkMode ? "#fff" : "#111827",
            }}
          >
            {t("Test Preview")}
          </Text>

          <View style={{ flexDirection: "row" }}>
            {Platform.OS === "web" && ENABLE_PRINT_BUTTON && (
              <TouchableOpacity
                onPress={handlePrint}
                style={{
                  marginRight: 12,
                  backgroundColor: "#111827",
                  paddingVertical: 6,
                  paddingHorizontal: 12,
                  borderRadius: 8,
                }}
              >
                <Text
                  style={{
                    color: "#fff",
                    fontWeight: "500",
                  }}
                >
                  {t("Print / Save PDF")}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              onPress={onClose}
              style={{
                backgroundColor: "#dc2626",
                paddingVertical: 6,
                paddingHorizontal: 12,
                borderRadius: 8,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <MaterialCommunityIcons
                name="close"
                size={16}
                color="#fff"
                style={{ marginRight: 4 }}
              />
              <Text
                style={{
                  color: "#fff",
                  fontWeight: "500",
                }}
              >
                {t("Close")}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Scrollable preview body inside the modal */}
        <ScrollView
          ref={scrollWrapperRef}
          style={{
            flex: 1,
            backgroundColor: isDarkMode ? "#1f2937" : "#fff",
            maxHeight: "100%",
            overflow: "auto",
          }}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingVertical: 16,
          }}
        >
          {/* --- Test header info --- */}
          <View
            style={{
              marginBottom: 20,
              padding: 12,
              backgroundColor: isDarkMode ? "#111827" : "#F5F5F5",
              borderRadius: 10,
              borderWidth: 1,
              borderColor: isDarkMode ? "#374151" : "#e5e7eb",
            }}
          >
            <Text
              style={{
                fontSize: 18,
                fontWeight: "600",
                marginBottom: 4,
                color: isDarkMode ? "#fff" : "#111827",
              }}
            >
              🧪 {testDetails.title || t("Untitled")}
            </Text>

            {!!testDetails.teacher_email && (
              <Text
                style={{
                  color: isDarkMode ? "#fff" : "#111827",
                  marginBottom: 2,
                }}
              >
                🧑‍🏫 {t("Teacher:")} {testDetails.teacher_email}
              </Text>
            )}

            {!!testDetails.description && (
              <Text
                style={{
                  color: isDarkMode ? "#fff" : "#111827",
                  marginBottom: 2,
                }}
              >
                📝 {testDetails.description}
              </Text>
            )}

            {!!testDetails.duration_minutes && (
              <Text
                style={{
                  color: isDarkMode ? "#fff" : "#111827",
                  marginBottom: 2,
                }}
              >
                🕒 {t("Duration:")} {testDetails.duration_minutes}{" "}
                {t("minutes")}
              </Text>
            )}

            {(testDetails.start_time || testDetails.end_time) && (
              <Text
                style={{
                  color: isDarkMode ? "#fff" : "#111827",
                  marginBottom: 2,
                }}
              >
                📅 {t("Available:")}{" "}
                {testDetails.start_time
                  ? new Date(
                      testDetails.start_time
                    ).toLocaleString()
                  : "N/A"}{" "}
                –{" "}
                {testDetails.end_time
                  ? new Date(
                      testDetails.end_time
                    ).toLocaleString()
                  : "N/A"}
              </Text>
            )}

            {Array.isArray(testDetails.chapters) &&
              testDetails.chapters.length > 0 && (
                <View style={{ marginTop: 5 }}>
                  <Text
                    style={{
                      color: isDarkMode ? "#fff" : "#111827",
                      marginBottom: 4,
                    }}
                  >
                    📚 {t("Chapters:")}
                  </Text>
                  {testDetails.chapters.map((ch, idx) => (
                    <Text
                      key={idx}
                      style={{
                        marginLeft: 10,
                        color: isDarkMode ? "#fff" : "#111827",
                      }}
                    >
                      • Chapter ID: {ch.chapter_id}{" "}
                      {ch.topic_id ? `→ Topic ID: ${ch.topic_id}` : ""}
                    </Text>
                  ))}
                </View>
              )}
          </View>

          {/* --- Questions in read-only mode (previewStatic) --- */}
          <QuestionDisplay
            mode="previewStatic"
            quiz={quiz}
            isDarkMode={false} // Force light mode – KaTeX equations render with black text
            userAnswers={emptyObj}
            answerFeedback={emptyObj}
            showHints={emptyObj}
            showSolutions={emptyObj}
            toggleHint={noop}
            toggleSolution={noop}
            handleAnswerChange={noop}
            handleMCQSelection={noop}
            checkTextAnswer={noop}
          />
        </ScrollView>
      </View>
    </View>
  );
}


